import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";

import {
    Accordion,
    AccordionContext,
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    OverlayTrigger,
    Spinner,
    Tooltip,
    useAccordionButton
} from "react-bootstrap";
import CarePathPrimary from "./carePathPrimary";
import CarePathOverdue from "./carePathOverdue";
import CarePathClosed from "./carePathClosed";
import CarePathKebab from "./carePathKebab";

import CarePathStep from "./CarePathStep";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import axios from "../../../../../utils/apiClient";
import {useDispatch, useSelector} from "react-redux";
import {setCareProgramModalOpen, setReloadWorklist} from "../../../../../actions/manageVisitActions";
import {useManageVisitContext} from "../../context/ManageVisitContext/ManageVisit.context";
import {
    useCMWorklistContext
} from "../../../../caremgmt/worklist/components/CMWorklistModals/context/CMWorklist.context";
import {formatDateModernLocal, getCarePathsColors} from "../../../../../utils/util";
import {useAuth} from "../../../../../context/authContext";
import {AppPropertiesUtil} from "../../../../../utils/localStorageUtil";
import isEmpty from "is-empty";

const componentName = "careProgram";
export const getCareProgramErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.errorMessage ||
  error?.response?.data?.error ||
  error?.message ||
  "Unable to update care program.";

const addSteps = (list) => {
  let subStep = 0;
  let mainStep = 0;
  let finalList = [];
  list.forEach((cp) => {
    if (cp.subStep) {
      const subSTepNum = mainStep + "." + subStep;
      cp.stepNumber = Number(subSTepNum);
      subStep++;
    } else {
      mainStep++;
      cp.stepNumber = mainStep;
      subStep = 1;
    }
    finalList.push(cp);
  });
  return finalList;
};

function PlusMinusToggle({ children, eventKey, callback, careProgramId }) {
  const { activeEventKey } = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(
    eventKey,
    () => callback?.(eventKey),
  );

  const isCurrentEventKey = activeEventKey?.includes(eventKey);
  return (
    <Button
      variant="link"
      className="d-flex align-items-center text-dark text-decoration-none"
      id={`${componentName}-carePathButton-${careProgramId}`}
      onClick={decoratedOnClick}
    >
      <i
        className={
          isCurrentEventKey
            ? "fa fa-chevron-down me-4"
            : "fa fa-chevron-right me-4"
        }
      ></i>
      {children}
    </Button>
  );
}

PlusMinusToggle.propTypes = {
  children: PropTypes.node,
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  callback: PropTypes.func,
  careProgramId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CarePathStepsList = ({ steps, renderStep, onMoveStep, dragDisabled }) => {
  const [draggingIndex, setDraggingIndex] = useState(null);

  const handleDrop = (destinationIndex) => {
    if (draggingIndex == null || draggingIndex === destinationIndex) {
      setDraggingIndex(null);
      return;
    }

    onMoveStep({
      source: { index: draggingIndex },
      destination: { index: destinationIndex },
    });
    setDraggingIndex(null);
  };

  return (
    <div>
      {steps.map((step, index) => (
        <div
          key={step.id}
          draggable={!dragDisabled}
          onDragStart={() => setDraggingIndex(index)}
          onDragOver={(event) => {
            if (!dragDisabled) {
              event.preventDefault();
            }
          }}
          onDrop={() => handleDrop(index)}
          onDragEnd={() => setDraggingIndex(null)}
        >
          {renderStep(step, index)}
        </div>
      ))}
    </div>
  );
};

CarePathStepsList.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })).isRequired,
  renderStep: PropTypes.func.isRequired,
  onMoveStep: PropTypes.func.isRequired,
  dragDisabled: PropTypes.bool.isRequired,
};

const shiftStepsFromIndex = (steps, startIndex) => {
  for (const step of steps.slice(startIndex)) {
    step.stepNumber = Math.round((step.stepNumber + 1) * 10) / 10;
  }
};

const shiftStepsFromNumber = (steps, number, comparison) => {
  for (const step of steps) {
    if (comparison(step.stepNumber, number)) {
      step.stepNumber = Math.round((step.stepNumber + 1) * 10) / 10;
    }
  }
};

export const buildCarePathStepsForNewStep = (carePathSteps, stepObj, index, action, number) => {
  const newState = carePathSteps.map(step => ({ ...step }));
  const newStep = { ...stepObj };

  if (action === "before") {
    if (index === 0) {
      shiftStepsFromIndex(newState, 0);
      newStep.stepNumber = number;
      newState.unshift(newStep);
    } else {
      shiftStepsFromNumber(newState, number, (stepNumber, targetNumber) => stepNumber >= targetNumber);
      newStep.stepNumber = number;
      newState.splice(index, 0, newStep);
    }
  } else if (action === "addCarePathStep") {
    newState.splice(newState.length + 1, 0, newStep);
  } else {
    shiftStepsFromNumber(newState, number, (stepNumber, targetNumber) => stepNumber > targetNumber);
    newStep.stepNumber = Math.floor(number + 1);
    newState.splice(index + 1, 0, newStep);
  }

  return { newState, newStep };
};

const buildUpdatedStep = (oldObj, stepObj) => {
  const newObj = {};
  Object.keys(oldObj).forEach((key) => {
    newObj[key] = key in stepObj ? stepObj[key] : oldObj[key];
  });
  return newObj;
};

const CareProgram = ({
  careManagementsPkg,
  patient,
  selectedTab,
  careProgram,
  patientInformation,
  selectedFilter,
  setRefreshFilter,
  open,
  setOpenStates,
  idx,
  filterResult
}) => {
  const [notesAdded, setNotesAdded] = useState([]);
  const [selectedCarePath, setSelectedCarePath] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const completedHidden = false;
  const [carePathSteps, setCarePathSteps] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [loadingPath, setLoadingPath] = useState(false);
  const dispatch = useDispatch();
  const manageVisitState = useManageVisitContext();
  const [customKey, setCustomKey] = useState('');
  const [blockDrag, setBlockDrag] = useState(false);

  const auth = useAuth();

  const authorizedCareProgramWrite = auth.hasAuthority("AUTH_PD_CARE_PROGRAMS_WRITE_ACCESS");
  const patientId = patient?.id;
  const [selectedExpired, setSelectedExpired] = useState(false);
  const [selectedExpiredDate, setSelectedExpiredDate] = useState(null);
  const [selectedExpiredSource, setSelectedExpiredSource] = useState(null);
  const [expandAll, setExpandAll] = useState(false);
  const [activeKey, setActiveKey] = useState([]);
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const mountedRef = useRef(true);
  const loadedCarePathIdRef = useRef(null);
  const loadingCarePathIdRef = useRef(null);

  const [careProgramObject, setCareProgramObject] = useState({
    show: false,
    careProgram: null,
    action: '',
    title: ''
  });

  const {
    toggleSnoozedModal,
    setstep,
    toggleCustomStatus
  } = useCMWorklistContext();
  const snoozedModal = useSelector((state) => state?.manageVisitReducer?.snoozedModal);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (selectedTab && patientId != null) {
      setNotesAdded([]);
    }

  }, [patientId, selectedTab]);

  useEffect(() => {
    setCustomKey(Math.random())
  }, [selectedFilter])

  const ref = useRef(null);
  const redirectedRef = useRef(null);
  const redirectID = sessionStorage.getItem('itemID');

  useEffect(() => {
    if (redirectedRef.current ) {
      redirectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
      setTimeout(() => {
        const element = document.getElementById(`${componentName}-carePathButton-${careProgram.id}`)
        element.click()
      }, 1000);
    }
  }, [careProgram.id])

  const loadCarePathSteps = useCallback((cp) => {
    const carePath = cp || selectedCarePath;
    const carePathId = carePath?.id;
    if (!carePathId || loadedCarePathIdRef.current === carePathId || loadingCarePathIdRef.current === carePathId) {
      return;
    }
    loadingCarePathIdRef.current = carePathId;
    setLoadingPath(true);
    setIsSearching(true);
    if (ref?.current) {
      ref.current.clear();
    }
    setSelectedCarePath(carePath);
    const sanitizedCarePathInstanceId = encodeURIComponent(carePathId);
    axios
      .get(
        `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/care-path-instance-steps?carePathInstanceId=${sanitizedCarePathInstanceId}`,
        {
          headers: {},
        }
      )
      .then((resp) => {
        if (!mountedRef.current) {
          return;
        }
        let options = addSteps(resp.data);
        setCarePathSteps(options);
        loadedCarePathIdRef.current = carePathId;
        setLoadingPath(false);
        setIsSearching(false);
      })
      .catch((error) => {
        if (mountedRef.current) {
          setLoadingPath(false);
          setIsSearching(false);
          manageVisitState.persistErrorMessage(error?.response?.data?.message);
        }
      })
      .finally(() => {
        if (loadingCarePathIdRef.current === carePathId) {
          loadingCarePathIdRef.current = null;
        }
      });
  }, [manageVisitState, ref, selectedCarePath]);

  const handleChangeCarePath = useCallback((cp, idx) => {
    const isOpening = !open?.includes(idx);
    setOpenStates(prevOpen => {
      const hasIndex = prevOpen.indexOf(idx);
      return hasIndex === -1
        ? [...prevOpen, idx]
        : prevOpen.filter(num => num !== idx);
    });
    if (isOpening && !snoozedModal) {
      loadCarePathSteps(cp);
    }
  }, [loadCarePathSteps, open, setOpenStates, snoozedModal]);

  const handleRefreshSteps = (cp) => {
    setLoadingPath(true);
    const sanitizedCarePathInstanceId = encodeURIComponent(cp?.id);
    return axios
      .get(
        `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/care-path-instance-steps?carePathInstanceId=${sanitizedCarePathInstanceId}`,
        {
          headers: {},
        }
      )
      .then((resp) => {
        if (!mountedRef.current) {
          return;
        }
        let options = addSteps(resp.data);
        setCarePathSteps(options);
        setLoadingPath(false);
        patientInformation(patient);
      })
      .catch((error) => {
        manageVisitState.persistErrorMessage(error?.response?.data?.message)
      });
  };

  const handleStepMovement = (result) => {
    // Do nothing if there's no destination
    if (!result.destination) {
      return;
    }
  
    // Create a copy of the current state
    const newState = Array.from(carePathSteps);
  
    // Get the item being moved
    const [movedItem] = newState.splice(result.source.index, 1);
  
    // Insert the moved item at its new location
    newState.splice(result.destination.index, 0, movedItem);
  
    // Update the stepNumber property for all items
    newState.forEach((step, index) => {
      step.stepNumber = index + 1;
    });
  
    // Access the 'instance' or 'carePathInstanceId'
    const instance = movedItem.carePathInstanceId;
  
    // Update the state
    setCarePathSteps([...newState]);
  
    // If the moved item has a carePathInstanceId, call updateStepAction
    if (instance) {
      updateStepAction(newState, instance);
    }
  };

  const handleStepRemoval = (step) => {
    let newState = carePathSteps;
    let instance = step.carePathInstanceId;
    newState.forEach((stp, i) => {
      if (stp.stepNumber === step.stepNumber) {
        stp.carePathStepDeleteReason = step.deletedReason
        stp.deleted = step.deleted

      }
    });
    removeStepAction(newState, instance);
    setCarePathSteps([...newState]);
  }

  const submitNewStep = (stepObj, carePathSteps) => {
    let carePathStepDto = stepObj;
    const index = carePathSteps.findIndex(object => {
      return stepObj.stepNumber === object.stepNumber;
    });
    const sanitizedCarePathInstanceId = encodeURIComponent(selectedCarePath.id);
    const sanitizedCarePathStepNumber = encodeURIComponent(index);
    return axios
      .post(
        `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/add-care-path-instance-step?carePathInstanceId=${sanitizedCarePathInstanceId}&carePathStepNumber=${sanitizedCarePathStepNumber}`,
        carePathStepDto,
        { headers: {} }
      )
      .then(() => {
        return handleRefreshSteps(selectedCarePath);
      })
      .catch((error) => {
        manageVisitState.persistErrorMessage(getCareProgramErrorMessage(error));
        throw error;
      });
  };

  const saveNewStep = async (stepObj, index, action, number) => {
    if (action === 'delete') {
      handleStepRemoval(stepObj);
    }
    if (action !== "delete") {
      const { newState, newStep } = buildCarePathStepsForNewStep(carePathSteps, stepObj, index, action, number);
      await submitNewStep(newStep, newState);
    }
    dispatch(setCareProgramModalOpen(false))
  };

  const editExistingStep = (step, stepObj, index) => {
    let newState = carePathSteps;
    let oldObj = newState[index];
    let instance = step.carePathInstanceId;
    let newObj = buildUpdatedStep(oldObj, stepObj);
    newState[index] = newObj;
    setCarePathSteps([...newState]);
    updateStepAction(newState, instance);
    dispatch(setCareProgramModalOpen(false))
  };

  const handleStatusChange = (e, value, isCustomStatus, customStatus) => {
    let newState = [...carePathSteps];

    newState.forEach((stp) => {
      if (stp.stepNumber === value.stepNumber) {
        value.carePathStepStatus = e;
      }
    });

    const isLastActiveStep = newState.every(e =>
      e.carePathStepStatus === 'COMPLETED' ||
      e.carePathStepStatus === 'CANCELLED' ||
      e.carePathStepStatus === 'SKIPPED'
    )
    if (isLastActiveStep) {
      setPendingTransaction(() => () => changeStatus(e, value, isCustomStatus, customStatus, newState, false))
      openCloseProgram(careProgram, 'closeCareProgram', 'Close Care Program');
      handleModal();
    } else {
      changeStatus(e, value, isCustomStatus, customStatus)
    }
  };

  const changeStatus = (e, value, isCustomStatus, customStatus, newState, reload = true) => {
    const body = {
      action: "SET_STATUS",
      careProviderId: value.assignedTo ? value.assignedTo.id : "",
      text: null,
      status: e,
      assignToId: null,
      stepCustomStatus: isCustomStatus ? customStatus : null,
    };
    if (e === 'SNOOZED') {
      toggleCustomStatus(customStatus)
      if (!snoozedModal) {
        toggleSnoozedModal(true)
      }
    } else {
      const sanitizedCarePathInstanceId = encodeURIComponent(value.carePathInstanceId);
      const sanitizedCarePathStepId = encodeURIComponent(value.id);
      axios
        .put(
          `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/update-care-path-instance-step?carePathInstanceId=${sanitizedCarePathInstanceId}&carePathStepId=${sanitizedCarePathStepId}`,
          body,
          { headers: {} }
        )
        .then((res) => {
          newState.forEach((stp) => {
            if (stp.stepNumber === res.data.result.stepNumber) {
              stp.stepCustomState = res.data.result.stepCustomState;
            }
          });
          setCarePathSteps([...newState]);
        })
        .catch((error) => {
          manageVisitState.persistErrorMessage(error?.response?.data?.message)
        })
        .finally(() =>  reload && dispatch(setReloadWorklist(true)))
    }
    setPendingTransaction(null)
  }

  const removeStepAction = (newObj, instance) => {
    const sanitizedCarePathInstanceId = encodeURIComponent(instance);
    axios
      .put(
        `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/set-care-path-instance-steps?carePathInstanceId=${sanitizedCarePathInstanceId}`,
        newObj,
        { headers: {} }
      )
      .then((res) => {
        handleRefreshSteps(selectedCarePath);
      })
      .catch((error) => {
        manageVisitState.persistErrorMessage(error?.response?.data?.message)
      });
  };

  const updateStepAction = (newObj, instance) => {
    const sanitizedCarePathInstanceId = encodeURIComponent(instance);
    axios
      .put(
        `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths/set-care-path-instance-steps?carePathInstanceId=${sanitizedCarePathInstanceId}`,
        newObj,
        { headers: {} }
      )
      .then((res) => {
        handleRefreshSteps(selectedCarePath);
      })
      .catch((error) => {
        console.error('API Error:', error); // Debugging log
        manageVisitState.persistErrorMessage(error?.response?.data?.message);
      });
  };

  const openCloseProgram = (careProgram, action, title) => {
    dispatch(setCareProgramModalOpen(true));

    setCareProgramObject({
      ...careProgramObject,
      show: true,
      careProgram: careProgram,
      action: action,
      title: title
    });
  };

  const handleCheckClose = (careProgram, action, title) => {
    checkCanCloseCarePath(careProgram, action, title);
  };

  const handleChangeOutReach = () => {
    axios.patch(`${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-manager-workspace/care-path-status`, {
        carePathInstanceId: selectedCarePath.id,
        patientProgramId: selectedCarePath.patientProgramId,
        patientId: patient.id,
        userId: auth.getUserId(),
        carePathStatus: "OUTREACH_IN_PROGRESS"
      }, {
        headers: {
          "Content-Type": "application/json"
        },
      }).then(() => {
        careManagementsPkg.fetchData(patient.id)
        dispatch(setReloadWorklist(true))
      }).catch((error) => {
        manageVisitState.persistErrorMessage(error?.response?.data?.message)
      });
  }

  const handleModal = () => {
    setSelectedExpired(false);
    setSelectedExpiredDate(null);
    setSelectedExpiredSource(null);
  };

  const checkCanCloseCarePath = (selectedCarePath, action, title) => {
    const baseURLCP = `${process.env.REACT_APP_REST_API_BASE_URL}/v1/care-paths`;
    const payLoad = {
      carePathInstanceId: selectedCarePath.id,
    };
    const url = `${baseURLCP}/check-close-care-path`;
    axios
      .post(url, payLoad, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (isEmpty(response?.data?.errorMessage)) {
          openCloseProgram(selectedCarePath, action, title);
          handleModal();
        } else {
          manageVisitState.persistErrorMessage(response.data.errorMessage);
          handleRefreshSteps(selectedCarePath);
        }
      })
      .catch((error) => {
        setErrorMessage(error);
      });
  };

  const toggleAll = (state) => {
    setExpandAll(state)
    if (state) {
      setActiveKey(carePathSteps.map((_,idx) => idx));
    } else {
      setActiveKey([])
    }
  };

  const carePathStatusesFeatureFlag = AppPropertiesUtil.get("newCarePathStatuses.feature.enabled");
  const hasCarePathSteps = carePathSteps?.length;
  const canShowStatusActions = careProgram?.isActive && careProgram?.overdue !== true && careProgram?.closed !== true;
  const writeAccessStatusStyle = authorizedCareProgramWrite
    ? { opacity: 1, cursor: 'pointer', pointerEvents: 'auto' }
    : { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' };

  const stopAccordionToggle = (event) => {
    event.stopPropagation();
  };

  const renderCarePathStep = (step, index) => {
    const commonStepProps = {
      step,
      setstep,
      handleStepMovement,
      handleStatusChange,
      index,
      pathsLength: carePathSteps.length,
      saveNewStep,
      editExistingStep,
      notesAdded,
      setNotesAdded,
      selectedPath: selectedCarePath,
      loading: loadingPath,
      handleChangeCarePath,
      errorMessage,
      patientInformation,
      patient,
      setBlockDrag,
      setActiveKey,
      activeKey,
      toggleAll,
      expandAll,
    };

    if (completedHidden) {
      if (step.carePathStepStatus !== "COMPLETED") {
        return <CarePathStep {...commonStepProps} />;
      }

      return null;
    }

    return (
      <CarePathStep
        {...commonStepProps}
        careProgram={careProgram}
        handleRefreshSteps={() => handleRefreshSteps({ id: step.carePathInstanceId })}
      />
    );
  };

  return (
    <div ref = {redirectID && careProgram.id === redirectID ? redirectedRef : null} key={customKey}>
      <Accordion className="mb-3" activeKey={open} alwaysOpen>
        <Card className="rounded-3 p-2">
          <Card.Header
            id={`${componentName}-carePathHead-${careProgram.id}`}
            onClick={() => handleChangeCarePath(careProgram, idx)}
            className="d-flex justify-content-between align-items-center bg-transparent border-0"
          >
            <PlusMinusToggle eventKey={idx} careProgramId={careProgram.id}>
              <span className="d-inline-block text-start">
                <span id={`${componentName}-carePathTitleLabel`} className="fs-5 fw-bold mb-1 d-block lh-1">{careProgram?.name}</span>
                <span id={`${componentName}-carePathAssigned`} className="text-muted">{careProgram.assignedTo ? careProgram.assignedTo.firstName + " " + careProgram.assignedTo.lastName : "Unassigned"}</span>
                <span id={`${componentName}-carePathDaysOpened`} className="text-muted ms-lg-5">
                  {careProgram.isClosed ? null : "Days Opened: " + (careProgram.carePathElapsedTime??'')}
                </span>
              </span>
            </PlusMinusToggle>
            {isSearching &&
              <Spinner
                className="me-3"
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            }
            <div className="d-flex justify-content-end align-items-center">
              {canShowStatusActions &&
                   <Dropdown onClick={stopAccordionToggle}>
                      <Dropdown.Toggle
                        className='badge rounded-pill border-0 me-4 status-pill cursor-p'
                        as={Badge}
                        id="dropdown-basic"
                        style={{
                          color: getCarePathsColors(careProgram?.carePathStatus).font,
                          ...writeAccessStatusStyle,
                        }}
                        ref={(el) => {
                          if (el) {
                            el.style.setProperty(
                              'background-color',
                              getCarePathsColors(careProgram.carePathStatus).bg,
                              'important'
                            )
                          }
                        }}
                      >
                        {careProgram?.carePathStatus && `${careProgram?.carePathStatus} on ${formatDateModernLocal(careProgram?.carePathStatusDate)}`}
                        {!careProgram?.carePathStatus && `Hub Assigned on ${formatDateModernLocal(careProgram?.startedAt)}`}
                        <i className={`fa fa-angle-down`} style={{ marginLeft:'5px' }}/>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {carePathStatusesFeatureFlag && <>
                          <Dropdown.Item onClick={() => openCloseProgram(careProgram, 'enroll', 'Enroll Member in Care Program')}>Enroll Member</Dropdown.Item> 
                          <Dropdown.Item onClick={() => handleChangeOutReach()}>Outreach In Progress</Dropdown.Item>
                        </>}
                        <Dropdown.Item onClick={() => handleCheckClose(careProgram, 'closeCareProgram', 'Close Care Program')}>Close Care Program</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
              }
              {careProgram?.primary && <CarePathPrimary />}
              {careProgram?.isActive && (
                  careProgram?.overdue
                  || careProgram?.carePathStepsAggregatorDto?.currentStep?.carePathStepStatus === "OVERDUE"
                  || careProgram?.carePathStepsAggregatorDto?.nextStep?.carePathStepStatus === "OVERDUE"
                  || careProgram?.carePathStepsAggregatorDto?.prevStep?.carePathStepStatus === "OVERDUE"
                  || careProgram?.carePathStepsAggregatorDto?.earliestDueDateStep?.carePathStepStatus === "OVERDUE"
              ) && <CarePathOverdue />}
              {(careProgram?.closed || !careProgram?.isActive) && careProgram?.closedDate && <CarePathClosed closedDate={careProgram?.closedDate} />}
              {(careProgram?.closed || !careProgram?.isActive) && !careProgram?.closedDate && <CarePathClosed closedDate={formatDateModernLocal(careProgram?.carePathStatusDate)} />}
              <CarePathKebab
                careProgram={careProgram}
                patient={patient}
                selectedCarePath={selectedCarePath}
                setSelectedCarePath={setSelectedCarePath}
                careManagementsPkg={careManagementsPkg}
                saveNewStep={saveNewStep}
                step={carePathSteps}
                patientInformation={patientInformation}
                setRefreshFilter={setRefreshFilter}
                setCareProgrammObject={setCareProgramObject}
                careProgramObject={careProgramObject}
                selectedExpired={selectedExpired}
                setSelectedExpired={setSelectedExpired}
                selectedExpiredDate={selectedExpiredDate}
                setSelectedExpiredDate={setSelectedExpiredDate}
                selectedExpiredSource={selectedExpiredSource}
                setSelectedExpiredSource={setSelectedExpiredSource}
                pendingTransaction={pendingTransaction}
                filterResult={filterResult}
              />
            </div>
          </Card.Header>
          <Accordion.Collapse eventKey={idx}>
            <Card.Body>
              <Col md={12} className='text-end mb-3'>
                <OverlayTrigger
                  placement="auto"
                  overlay={
                    <Tooltip>
                      Expand / Collapse All
                    </Tooltip>
                  }
                >
                  <Button
                    variant="light"
                    aria-label="Expand or collapse all care path steps"
                    className="border text-dark badge badge-pill mt-2 font-sm"
                    onClick={() => toggleAll(!expandAll)}
                    style={{maxWidth: 'fit-content'}}
                  >
                    <i className='fas fa-angle-double-up'/>
                    <i className='fas fa-angle-double-down'/>
                  </Button>
                </OverlayTrigger>
              </Col>
            {hasCarePathSteps ? (
              <CarePathStepsList
                steps={carePathSteps}
                renderStep={renderCarePathStep}
                onMoveStep={handleStepMovement}
                dragDisabled={!authorizedCareProgramWrite || blockDrag}
              />
              ) : null}
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};

CareProgram.propTypes = {
  careManagementsPkg: PropTypes.shape({
    state: PropTypes.shape({
      data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    }),
    fetchData: PropTypes.func,
  }).isRequired,
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  selectedTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  careProgram: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    assignedTo: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    isClosed: PropTypes.bool,
    carePathElapsedTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    overdue: PropTypes.bool,
    closed: PropTypes.bool,
    isActive: PropTypes.bool,
    carePathStatus: PropTypes.string,
    carePathStatusDate: PropTypes.string,
    startedAt: PropTypes.string,
    primary: PropTypes.bool,
    carePathStepsAggregatorDto: PropTypes.shape({
      currentStep: PropTypes.shape({ carePathStepStatus: PropTypes.string }),
      nextStep: PropTypes.shape({ carePathStepStatus: PropTypes.string }),
      prevStep: PropTypes.shape({ carePathStepStatus: PropTypes.string }),
      earliestDueDateStep: PropTypes.shape({ carePathStepStatus: PropTypes.string }),
    }),
    closedDate: PropTypes.string,
  }).isRequired,
  patientInformation: PropTypes.func.isRequired,
  selectedFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  setRefreshFilter: PropTypes.func,
  open: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  setOpenStates: PropTypes.func.isRequired,
  idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  filterResult: PropTypes.func,
};

export default CareProgram;
